import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const HEIGHT = 240;
const MARGIN = { top: 12, right: 12, bottom: 28, left: 52 };
const TICK_COUNT = 4;

// Rounds the axis maximum up to 1, 2, 2.5 or 5 times a power of ten so the
// gridlines land on readable values.
function niceMaximum(value) {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value / TICK_COUNT));
  const step = [1, 2, 2.5, 5, 10]
    .map((factor) => factor * magnitude)
    .find((candidate) => candidate * TICK_COUNT >= value);
  return step * TICK_COUNT;
}

function formatDate(isoDate, options) {
  return new Date(`${isoDate}T00:00:00Z`).toLocaleDateString(undefined, {
    timeZone: 'UTC',
    ...options,
  });
}

function useElementWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width),
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

// Long ranges arrive as buckets of several days, labeled by their first day.
export function tooltipDate(isoDate, bucketDays) {
  const full = { month: 'short', day: 'numeric', year: 'numeric' };
  if (bucketDays <= 1) return formatDate(isoDate, { weekday: 'short', ...full });
  const end = new Date(`${isoDate}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() + bucketDays - 1);
  return `${formatDate(isoDate, { month: 'short', day: 'numeric' })} – ${formatDate(end.toISOString().slice(0, 10), full)}`;
}

/**
 * Single-series daily line chart with a hover crosshair and tooltip.
 * @param {object[]} props.data - [{ date: 'YYYY-MM-DD', ...values }]
 * @param {(day: object) => number|null} props.value - reads the plotted value; null plots as zero
 * @param {(value: number) => string} props.format - formats values for axis and tooltip
 * @param {number} [props.bucketDays] - days each point covers
 * @param {string} [props.color] - line color; defaults to the primary color
 */
export default function DailyMetricChart({ data, value: readValue, format, label, bucketDays = 1, color: lineColor }) {
  const theme = useTheme();
  const [containerRef, width] = useElementWidth();
  const [hoverIndex, setHoverIndex] = useState(null);
  const value = (day) => readValue(day) || 0;

  if (!data.length) {
    return (
      <Box
        ref={containerRef}
        sx={{ height: HEIGHT, display: 'grid', placeItems: 'center' }}
      >
        <Typography color="text.secondary">
          No daily data for this period yet.
        </Typography>
      </Box>
    );
  }

  const plotWidth = Math.max(width - MARGIN.left - MARGIN.right, 0);
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const maximum = niceMaximum(Math.max(...data.map(value)));
  const x = (index) =>
    MARGIN.left +
    (data.length === 1 ? plotWidth / 2 : (index / (data.length - 1)) * plotWidth);
  const y = (amount) => MARGIN.top + plotHeight - (amount / maximum) * plotHeight;

  const linePath = data
    .map((day, index) => `${index ? 'L' : 'M'}${x(index)},${y(value(day))}`)
    .join(' ');
  const areaPath = `${linePath} L${x(data.length - 1)},${y(0)} L${x(0)},${y(0)} Z`;
  const ticks = Array.from(
    { length: TICK_COUNT + 1 },
    (_, index) => (maximum / TICK_COUNT) * index,
  );
  const labelEvery = Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(plotWidth / 90))));
  const color = lineColor || theme.palette.primary.main;
  const muted = theme.palette.text.secondary;
  const hovered = hoverIndex === null ? null : data[hoverIndex];

  function handlePointer(event) {
    const bounds = event.currentTarget.getBoundingClientRect();
    const position = event.clientX - bounds.left - MARGIN.left;
    const index = Math.round((position / plotWidth) * (data.length - 1));
    setHoverIndex(Math.min(Math.max(index, 0), data.length - 1));
  }

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%' }}>
      {width > 0 && (
        <svg
          width={width}
          height={HEIGHT}
          role="img"
          aria-label={`${label} per day`}
          onPointerMove={handlePointer}
          onPointerLeave={() => setHoverIndex(null)}
          style={{ display: 'block', touchAction: 'pan-y' }}
        >
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={MARGIN.left}
                x2={MARGIN.left + plotWidth}
                y1={y(tick)}
                y2={y(tick)}
                stroke={theme.palette.divider}
                strokeDasharray={tick ? '2 4' : undefined}
              />
              <text
                x={MARGIN.left - 8}
                y={y(tick)}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={11}
                fill={muted}
              >
                {format(tick)}
              </text>
            </g>
          ))}
          {data.map((day, index) =>
            index % labelEvery === 0 ? (
              <text
                key={day.date}
                x={x(index)}
                y={HEIGHT - 8}
                textAnchor={index === 0 ? 'start' : x(index) > width - 40 ? 'end' : 'middle'}
                fontSize={11}
                fill={muted}
              >
                {formatDate(day.date, { month: 'short', day: 'numeric' })}
              </text>
            ) : null,
          )}
          <path d={areaPath} fill={color} fillOpacity={0.12} />
          <path
            d={linePath}
            fill="none"
            stroke={color}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {hovered && (
            <g pointerEvents="none">
              <line
                x1={x(hoverIndex)}
                x2={x(hoverIndex)}
                y1={MARGIN.top}
                y2={MARGIN.top + plotHeight}
                stroke={muted}
                strokeOpacity={0.5}
              />
              <circle
                cx={x(hoverIndex)}
                cy={y(value(hovered))}
                r={4}
                fill={color}
                stroke={theme.palette.background.paper}
                strokeWidth={2}
              />
            </g>
          )}
        </svg>
      )}
      {hovered && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: x(hoverIndex),
            transform: `translateX(${hoverIndex > data.length / 2 ? 'calc(-100% - 12px)' : '12px'})`,
            pointerEvents: 'none',
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 2,
            px: 1.25,
            py: 0.75,
            whiteSpace: 'nowrap',
          }}
        >
          <Typography variant="caption" color="text.secondary" component="div">
            {tooltipDate(hovered.date, bucketDays)}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {format(value(hovered))} {label.toLowerCase()}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
